import './Projects.scss';
import Letters from '../Letters';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import crypto from '../../assets/images/crypto-game.png';
import slither from '../../assets/images/slither.png';
import workDay from '../../assets/images/work-day-scheduler.png';
import password from '../../assets/images/password-gen.png';
import longhornLoop from '../../assets/images/longhorn-loop.png';

// Initials for the placeholder tile on projects with no screenshot yet.
const initialsOf = (title) =>
  title
    .split(/[\s-]+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

// One source of truth for the cards below. Adding a project means adding an
// entry here — no more copy-pasting markup four times. `image` and `demo` are
// both optional.
const projects = [
  {
    title: 'Longhorn Loop',
    role: 'Co-founder & Tech Lead',
    description:
      'A cross-platform mobile app for the UT Austin community, built with Longhorn Developers. I co-founded the idea and lead the build as Tech Lead, directing a team of six developer fellows. React Native and Expo on the front end, with an Express server, SQL storage, and Cloudflare handling serverless API routes, auth and delivery.',
    image: longhornLoop,
    alt: 'Longhorn Loop logo — the UT Tower drawn as a continuous loop',
    // The asset is a logo on a solid ground, not a screenshot: `contain` keeps
    // the tower and wordmark whole, and matching the tile to the logo's own
    // background makes the letterboxing invisible.
    imageFit: 'contain',
    mediaBackground: '#9d4b06',
    tags: ['React Native', 'Expo', 'TypeScript', 'SQL', 'Cloudflare'],
    demo: null,
    repo: 'https://github.com/Longhorn-Developers/Longhorn-Loop',
  },
  {
    title: 'WhataCrypto',
    description:
      'A cryptocurrency game that lets people make decisions about how and when to buy and sell crypto. It promotes strategic thinking and gives players a low-stakes environment to learn in.',
    image: crypto,
    alt: 'Screenshot of the WhataCrypto cryptocurrency game',
    tags: ['JavaScript', 'Game Logic'],
    demo: 'https://matthewevwalker.github.io/whatacrypto-crypto-game/',
    repo: 'https://github.com/MatthewEvWalker/whatacrypto-crypto-game',
  },
  {
    title: 'SlitherAI',
    description:
      'A single-page MERN application demonstrating a Deep Q-Network (DQN). It automates the snake game using qTables for reinforcement learning, so the snake teaches itself to play and improves over time.',
    image: slither,
    alt: 'Screenshot of the SlitherAI reinforcement learning snake game',
    tags: ['MERN', 'Reinforcement Learning', 'Deep Q-Network'],
    demo: null, // no deployed build — code only
    repo: 'https://github.com/MatthewEvWalker/Slither-AI',
  },
  {
    title: 'Work Day Scheduler',
    description:
      'A static site for planning your work day. It uses moment.js to display the current date and time, and local storage to keep your schedule between visits.',
    image: workDay,
    alt: 'Screenshot of the Work Day Scheduler application',
    tags: ['JavaScript', 'moment.js', 'Local Storage'],
    demo: 'https://matthewevwalker.github.io/work-scheduler-5/',
    repo: 'https://github.com/MatthewEvWalker/work-scheduler-5',
  },
  {
    title: 'Password Generator',
    description:
      'A beginner-friendly password generator that builds passwords between 8 and 128 characters, with options for uppercase, lowercase, numbers and special characters.',
    image: password,
    alt: 'Screenshot of the Password Generator application',
    tags: ['JavaScript', 'HTML', 'CSS'],
    demo: 'https://matthewevwalker.github.io/Password_Generator/',
    repo: 'https://github.com/MatthewEvWalker/Password_Generator',
  },
];

const Projects = () => {
  const [letterClass, setLetterClass] = useState('text-animate');

  useEffect(() => {
    const timer = setTimeout(() => {
      setLetterClass('text-animate-hover');
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container projects-page">
      <header className="page-header">
        <h1>
          <Letters
            letterClass={letterClass}
            letterArray={['M', 'y', ' ', 'P', 'r', 'o', 'j', 'e', 'c', 't', 's']}
            index={20}
          />
        </h1>
        <p className="lead">
          A selection of things I have built. Each card links to the live
          application and to the source on GitHub.
        </p>
      </header>

      <div className="project-grid">
        {projects.map(
          ({
            title,
            role,
            description,
            image,
            alt,
            imageFit,
            mediaBackground,
            tags,
            demo,
            repo,
          }) => (
          <article className="project-card" key={title}>
            {image ? (
              <div
                className={`project-media${
                  imageFit === 'contain' ? ' project-media--contain' : ''
                }`}
                style={
                  mediaBackground
                    ? { backgroundColor: mediaBackground }
                    : undefined
                }
              >
                <img src={image} alt={alt} loading="lazy" />
              </div>
            ) : (
              // Decorative only — the project title sits right below it.
              <div
                className="project-media project-media--placeholder"
                aria-hidden="true"
              >
                <span>{initialsOf(title)}</span>
              </div>
            )}

            <div className="project-body">
              {role && <span className="project-role">{role}</span>}
              <h2>{title}</h2>
              <p>{description}</p>

              <ul className="tag-list">
                {tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>

              <div className="project-links">
                {demo && (
                  <a
                    className="project-link primary"
                    href={demo}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    <span>Live demo</span>
                  </a>
                )}

                <a
                  className="project-link"
                  href={repo}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faGithub} />
                  <span>View code</span>
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Projects;
